import { useCallback, useEffect, useState, type RefObject } from "react";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import {
  SHOWCASE_SETTINGS,
  devicesFor,
  screenAfterDeviceChange,
  screensFor,
  showcaseCanCycle,
  type ShowcaseDevice,
  type ShowcasePage,
  type ShowcaseScreenId,
} from "./productShowcaseConfig";

/** True while the element is at least partly on screen. False during SSR. */
function useInView(ref: RefObject<Element>): boolean {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 0.25,
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);

  return inView;
}

/**
 * Showcase state: device mode, active screen and the autoplay clock.
 * The clock is a CSS animation on the active card's progress line; its end
 * calls `tick`, so pausing it (hover, nav focus, off screen, hidden tab)
 * freezes the timer exactly where it stopped. Picking a screen by hand stops
 * autoplay; the play button restarts it. A device with a single screen has
 * no autoplay and no play/pause control.
 */
export function useProductShowcase(page: ShowcasePage, sectionRef: RefObject<HTMLElement>) {
  const reducedMotion = usePrefersReducedMotion();
  const inView = useInView(sectionRef);
  const devices = devicesFor(page);
  const initialDevice = devices.includes(SHOWCASE_SETTINGS.defaultDevice) ? SHOWCASE_SETTINGS.defaultDevice : devices[0];
  const [device, setDevice] = useState<ShowcaseDevice>(initialDevice);
  const [activeId, setActiveId] = useState<ShowcaseScreenId>(() => screensFor(page, initialDevice)[0]);
  const [autoplay, setAutoplay] = useState(SHOWCASE_SETTINGS.autoplay);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);

  useEffect(() => {
    const update = () => setPageVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  const screens = screensFor(page, device);
  const activeIndex = Math.max(0, screens.indexOf(activeId));
  // One screen on this device: nothing to advance to, so no clock and no play/pause.
  const canCycle = showcaseCanCycle(page, device);
  const canAutoplay = autoplay && !reducedMotion && canCycle;

  const tick = useCallback(() => {
    setActiveId(screens[(activeIndex + 1) % screens.length]);
  }, [screens, activeIndex]);

  const select = useCallback((id: ShowcaseScreenId) => {
    setActiveId(id);
    setAutoplay(false);
  }, []);

  const changeDevice = useCallback(
    (next: ShowcaseDevice) => {
      setDevice(next);
      setActiveId((current) => screenAfterDeviceChange(page, current, next));
    },
    [page],
  );

  const toggleAutoplay = useCallback(() => setAutoplay((value) => !value), []);

  return {
    device,
    devices,
    screens,
    activeId: screens[activeIndex],
    activeIndex,
    autoplay: canAutoplay,
    canCycle,
    reducedMotion,
    playing: canAutoplay && inView && pageVisible && !hovered && !focused,
    inView,
    tick,
    select,
    changeDevice,
    toggleAutoplay,
    setHovered,
    setFocused,
  };
}
